/* ===================================================
   Page Panier Zoomalin — assets/cart-zoomalin.js
   Connecté à l'API Cart Shopify
   =================================================== */

(function () {
  'use strict';

  const wrapper = document.querySelector('[data-cart-section]');
  if (!wrapper) return;

  const SHIPPING_THRESHOLD = parseInt(wrapper.dataset.shippingThreshold, 10) || 5900;
  const CURRENCY = wrapper.dataset.currency || 'EUR';

  /* ====================================================
     Helpers
     ==================================================== */
  function formatMoney(cents) {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: CURRENCY,
    }).format(cents / 100);
  }

  function debounce(fn, delay) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  /* ====================================================
     API Shopify Cart
     ==================================================== */
  async function updateLine(lineIndex, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: lineIndex, quantity: quantity })
      });
      if (!response.ok) throw new Error('Erreur lors de la mise à jour du panier');
      return await response.json();
    } catch (e) {
      console.error('[Cart] updateLine failed', e);
      return null;
    }
  }

  async function getCart() {
    const response = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    return await response.json();
  }

  /* ====================================================
     Mise à jour de l'UI
     ==================================================== */
  function updateShippingBar(totalCents) {
    const bar = document.querySelector('[data-shipping-bar]');
    if (!bar) return;

    const remaining = SHIPPING_THRESHOLD - totalCents;
    const fill = bar.querySelector('[data-progress-fill]');
    const remainingEl = bar.querySelector('[data-shipping-remaining]');
    const headerLeft = bar.querySelector('.zm-shipping-header > span:first-child');

    if (remaining <= 0) {
      bar.classList.add('unlocked');
      if (fill) fill.style.width = '100%';
      if (headerLeft) {
        headerLeft.innerHTML = '🎉 <strong>Livraison offerte débloquée !</strong>';
      }
    } else {
      bar.classList.remove('unlocked');
      const progressPct = Math.min(100, (totalCents / SHIPPING_THRESHOLD) * 100);
      if (fill) fill.style.width = progressPct + '%';
      if (remainingEl) {
        remainingEl.textContent = formatMoney(remaining);
      } else if (headerLeft) {
        headerLeft.innerHTML = `🚚 Plus que <strong data-shipping-remaining>${formatMoney(remaining)}</strong> pour la livraison offerte`;
      }
    }
  }

  function updateTotals(cart) {
    const totalEl = document.querySelector('[data-cart-total]');
    const subtotalEl = document.querySelector('[data-cart-subtotal]');
    if (totalEl) totalEl.textContent = formatMoney(cart.total_price);
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.items_subtotal_price);
    updateShippingBar(cart.total_price);
  }

  function updateLinePrice(lineEl, cart) {
    const lineIndex = parseInt(lineEl.dataset.lineIndex, 10) - 1;
    const item = cart.items[lineIndex];
    if (!item) return;
    const priceEl = lineEl.querySelector('[data-line-price]');
    if (priceEl) priceEl.textContent = formatMoney(item.final_line_price);
  }

  /* ====================================================
     Handlers
     ==================================================== */
  const debouncedQtyUpdate = debounce(async (lineIndex, quantity, lineEl) => {
    lineEl.classList.add('is-loading');
    const cart = await updateLine(lineIndex, quantity);
    lineEl.classList.remove('is-loading');

    if (!cart) {
      // En cas d'erreur, on rafraîchit la page pour resynchroniser
      window.location.reload();
      return;
    }

    if (quantity === 0) {
      // Animation de suppression
      lineEl.classList.add('is-removing');
      setTimeout(() => window.location.reload(), 300);
      return;
    }

    updateLinePrice(lineEl, cart);
    updateTotals(cart);

    // Mise à jour du compteur du nombre d'articles dans le subtitle
    const subtitle = document.querySelector('.zm-subtitle');
    if (subtitle) {
      const word = cart.item_count > 1 ? 'articles' : 'article';
      subtitle.innerHTML = `${cart.item_count} ${word} • Vos produits vous attendent 🐾`;
    }

    // Mise à jour du badge du header (compatibilité méga menu)
    const badge = document.querySelector('[data-cart-count]');
    if (badge) badge.textContent = cart.item_count;

    // Émission d'un event pour les autres scripts (méga menu)
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
  }, 350);

  function handleQtyChange(lineEl, newQty) {
    const lineIndex = parseInt(lineEl.dataset.lineIndex, 10);
    const input = lineEl.querySelector('[data-qty-input]');
    if (input) input.value = newQty;
    debouncedQtyUpdate(lineIndex, newQty, lineEl);
  }

  /* ====================================================
     Listeners
     ==================================================== */
  wrapper.querySelectorAll('[data-cart-item]').forEach(lineEl => {
    const input = lineEl.querySelector('[data-qty-input]');
    const decBtn = lineEl.querySelector('[data-qty-action="decrease"]');
    const incBtn = lineEl.querySelector('[data-qty-action="increase"]');
    const removeBtn = lineEl.querySelector('[data-cart-remove]');

    if (decBtn) decBtn.addEventListener('click', () => {
      const newVal = Math.max(0, parseInt(input.value, 10) - 1);
      handleQtyChange(lineEl, newVal);
    });

    if (incBtn) incBtn.addEventListener('click', () => {
      const newVal = parseInt(input.value, 10) + 1;
      handleQtyChange(lineEl, newVal);
    });

    if (input) input.addEventListener('change', () => {
      const newVal = Math.max(0, parseInt(input.value, 10) || 0);
      handleQtyChange(lineEl, newVal);
    });

    if (removeBtn) removeBtn.addEventListener('click', () => {
      if (!confirm('Retirer cet article du panier ?')) return;
      handleQtyChange(lineEl, 0);
    });
  });

  /* ====================================================
     Code promo : redirection vers /discount/CODE
     ==================================================== */
  const promoToggle = wrapper.querySelector('[data-promo-toggle]');
  const promoInput = wrapper.querySelector('[data-promo-input]');
  const promoApply = wrapper.querySelector('[data-promo-apply]');

  if (promoToggle && promoInput) {
    promoToggle.addEventListener('click', () => {
      promoInput.classList.toggle('active');
      const icon = promoToggle.querySelector('.zm-promo-icon');
      if (icon) {
        icon.textContent = promoInput.classList.contains('active') ? '−' : '+';
      }
      if (promoInput.classList.contains('active')) {
        const field = promoInput.querySelector('input');
        if (field) setTimeout(() => field.focus(), 100);
      }
    });
  }

  function applyPromo() {
    const codeInput = wrapper.querySelector('#promo-code');
    if (!codeInput) return;
    const code = codeInput.value.trim();
    if (!code) {
      codeInput.focus();
      return;
    }
    // Redirection Shopify : applique le discount et redirige vers le panier
    window.location.href = `/discount/${encodeURIComponent(code)}?redirect=/cart`;
  }

  if (promoApply) promoApply.addEventListener('click', applyPromo);

  const promoField = wrapper.querySelector('#promo-code');
  if (promoField) {
    promoField.addEventListener('keydown', e => {
      if (e.key === 'Enter') {
        e.preventDefault();
        applyPromo();
      }
    });
  }

  /* ====================================================
     Note de commande (auto-save sur blur)
     ==================================================== */
  const noteField = wrapper.querySelector('textarea[name="note"]');
  if (noteField) {
    let originalNote = noteField.value;
    noteField.addEventListener('blur', async () => {
      if (noteField.value === originalNote) return;
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: noteField.value })
        });
        originalNote = noteField.value;
      } catch (e) {
        console.error('[Cart] note update failed', e);
      }
    });
  }
})();
